import type * as THREE from 'three'
import type { ViewId, ViewRect } from './view'

// ==================== Asset Identity ====================

/** 资产种类枚举（字符串字面量） */
export type AssetKind = 'pointCloud' | 'gaussian-splat' | 'cad' | 'mesh' | 'tiles'

/** 资产唯一 id */
export type AssetId = string

/** CSS 像素坐标系下的视口矩形 */
export interface ViewportRect {
  /** 左上角 x，单位 CSS 像素 */
  x: number
  /** 左上角 y，单位 CSS 像素 */
  y: number
  /** 宽度，单位 CSS 像素 */
  width: number
  /** 高度，单位 CSS 像素 */
  height: number
}

// ==================== Picking ====================

/** 拾取查询参数 */
export interface PickQuery {
  /** 归一化设备坐标，x/y 取值 [-1, 1] */
  ndc: THREE.Vector2
  /** 世界坐标系射线 */
  ray: THREE.Ray
  /** 从原始射线起点沿归一化方向计算的最小拾取距离，单位为世界坐标长度 */
  minDistance?: number
  /** 拾取使用的相机 */
  camera: THREE.Camera
  /** CSS 像素坐标，可选，供部分实现使用 */
  screen?: THREE.Vector2
  /** 视口矩形，可选 */
  viewport?: ViewportRect
  /** 当前拾取命中的 view id；多视口下优先使用该字段而不是隐式推断 active view */
  viewId?: ViewId
  /** 当前拾取使用的 view-local 屏幕矩形；多视口下不应再默认等同于整个 container */
  viewRect?: ViewRect
  /** 当前 view 的 layerMask；PickingService 会在拾取期间同步到 query.camera.layers */
  layerMask?: number
  /** 请求创建时冻结的默认 surface 像素比；异步 GPU pick 不得在 await 后重新读取 live DPR。 */
  pixelRatio?: number
  /** 拾取窗口像素尺寸（应为正奇数），默认实现可用于点云包围球扩张 */
  windowSize?: number
  /** 发起拾取的原生控制器来源，用于宿主侧隔离控制器拾取策略 */
  interactionSource?: 'spacemouse' | 'earth'
  /** 交互类型，用于区分点击/移动等场景下的精度与性能权衡 */
  interactionType?: 'click' | 'move' | 'auto'
  /** 中心球半径，用于扩大命中范围 */
  centerSphereRadius?: number
  /** 过滤的资产种类，只在指定 kind 中拾取 */
  kinds?: AssetKind[]
  /** 过滤的资产 id，只在指定资产中拾取 */
  assetIds?: AssetId[]
  /** 是否返回全部命中结果，false 只返回最近命中 */
  all?: boolean
  /** 仅使用 GPU 拾取，不就绪时返回 null 而非 CPU 回退 */
  gpuOnly?: boolean
  /** 是否允许当前查询在拾取路径上按需预热 GPU picker */
  prewarmGpu?: boolean
  /** 异步拾取取消信号；同步入口不会等待异步 provider。 */
  signal?: AbortSignal
}

/** 拾取点的属性字典（点云拾取时填充，其他资产类型为 undefined） */
/** 已知的拾取点属性 */
export interface KnownPickPointAttributes {
  /** LAS 分类码 */
  classification?: number
  /** 激光反射强度 */
  intensity?: number
  /** RGBA 颜色数组，分量取值 0~255 */
  rgba?: [number, number, number, number]
  /** 回波序号 */
  returnNumber?: number
  /** GPS 时间戳 */
  gpsTime?: number
}

/** 拾取点属性（已知属性 + 自定义扩展） */
export type PickPointAttributes = KnownPickPointAttributes & Record<string, unknown>

/** 拾取命中结果 */
export interface TilesPickSource {
  readonly assetId: AssetId
  readonly recordGeneration: number
  readonly viewId: ViewId
  readonly commitVersion: number
  readonly tileId: string
  readonly contentId: string
  readonly sourceUrl: string
  readonly primitiveId: string
  readonly triangleIndex: number
  readonly materialIndex: number
}

/** 拾取命中结果；Tiles 来源仅含值，不持有几何。 */
export interface PickResult {
  /** 命中资产 id */
  assetId: AssetId
  /** 命中资产种类 */
  kind: AssetKind
  /** 世界坐标命中点 */
  point: THREE.Vector3
  /** 命中距离（相机到命中点） */
  distance: number
  /** 命中点法线，无则缺省 */
  normal?: THREE.Vector3
  /** 点云命中的点索引 */
  pointIndex?: number
  /** CAD 命中的 entity id */
  entityId?: string
  /** 拾取点的属性（点云拾取时填充） */
  attributes?: PickPointAttributes
  tilesSource?: TilesPickSource
  /** 底层原始命中数据，引擎内部使用 */
  _raw?: unknown
}

/** 矩形框选拾取查询 */
export interface RectPickQuery {
  /** 矩形左上角 NDC */
  min: THREE.Vector2
  /** 矩形右下角 NDC */
  max: THREE.Vector2
  /** 使用的相机 */
  camera: THREE.Camera
  /** 视口矩形，可选 */
  viewport?: ViewportRect
  /** 多视口下的目标 view id */
  viewId?: ViewId
  /** 多视口下的 view 屏幕矩形 */
  viewRect?: ViewRect
  /** 渲染 layer 位掩码 */
  layerMask?: number
  /** 过滤的资产种类 */
  kinds?: AssetKind[]
  /** 仅使用 GPU 拾取；未就绪时返回空结果而不回退 CPU。 */
  gpuOnly?: boolean
  /** 异步框选最多返回的结果数。 */
  maxResults?: number
  /** 异步拾取取消信号。 */
  signal?: AbortSignal
}

// ==================== Asset Handles ====================

/** 资产句柄基础接口，所有资产 handle 扩展此接口 */
export interface AssetHandle {
  /** 资产唯一 id */
  readonly id: AssetId
  /** 资产种类 */
  readonly kind: AssetKind
  /** 资产显示名 */
  name: string
  /** 是否可见 */
  visible: boolean
  /** 资产局部坐标包围盒，尚未计算返回 null；世界范围由 object3D.matrixWorld 转换 */
  boundingBox: THREE.Box3 | null
  /** 挂载到场景的 Object3D，未挂载返回 null */
  readonly object3D: THREE.Object3D | null
}

// ==================== Clipping ====================

/** 裁切选择模式 */
export type ClipSelectionMode = 'polygon' | 'rectangle' | 'box3d'

/** 裁切应用模式，决定保留内侧还是外侧 */
export type ClipApplyMode = 'inside' | 'outside'

/** 裁切体所在坐标空间 */
export type ClipVolumeSpace = 'world' | 'screen'

/** 屏幕空间 2D 包围盒 */
export interface ClipAABB2D {
  /** 最小角点 */
  min: THREE.Vector2
  /** 最大角点 */
  max: THREE.Vector2
}

/** 裁切体描述 */
export interface ClipVolume {
  /** 几何类型 */
  type: 'box' | 'polygon'
  /** 世界变换矩阵 */
  matrix: THREE.Matrix4
  /** 多边形顶点（polygon 类型使用） */
  vertices?: THREE.Vector3[]
  /** 所属分组 id */
  groupId?: string
  /** 所属视图 id */
  viewId?: ViewId
  /** 裁切任务位掩码 */
  clipTask?: number
  /** 选择模式 */
  selectionMode?: ClipSelectionMode
  /** 所在坐标空间 */
  space?: ClipVolumeSpace
  /** 屏幕空间裁切需要的视图矩阵 */
  viewMatrix?: THREE.Matrix4
  /** 屏幕空间裁切需要的投影矩阵 */
  projMatrix?: THREE.Matrix4
  /** 屏幕空间 2D 包围盒 */
  aabb?: ClipAABB2D
}

/** 裁切绘制过程中的草稿状态 */
export interface ClipDraftState {
  /** 当前选择模式 */
  selectionMode: ClipSelectionMode
  /** 当前应用模式 */
  clipMode: ClipApplyMode
  /** 已确认的点 */
  points: ReadonlyArray<THREE.Vector3>
  /** 鼠标跟随的预览点 */
  previewPoint: THREE.Vector3 | null
  /** 正在编辑的 box 世界矩阵 */
  boxMatrix: THREE.Matrix4 | null
  /** 当前草稿是否可确认 */
  canConfirm: boolean
}

/** box3d 编辑器状态 */
export interface ClipBoxEditorState {
  /** 编辑器是否激活 */
  active: boolean
  /** 是否正在拖拽变换 */
  dragging: boolean
  /** 当前选择模式 */
  selectionMode: ClipSelectionMode
  /** 当前 draft box 的世界矩阵 */
  boxMatrix: THREE.Matrix4 | null
  /** 当前 draft box 的位置 */
  boxPosition: THREE.Vector3 | null
  /** 当前 draft box 的缩放 */
  boxScale: THREE.Vector3 | null
  /** 当前 draft box 的旋转 */
  boxQuaternion: THREE.Quaternion | null
}

// ==================== Disposable ====================

/** 可释放资源接口 */
export interface Disposable {
  /** 释放底层资源，调用后对象不应再被使用 */
  dispose(): void
}

/** 工具会话：activateTool 的增强入口，提供结果订阅和取消控制 */
export interface ToolSession<T = unknown> extends Disposable {
  /** 工具名称 */
  readonly name: string
  /** 所属互斥分组 */
  readonly group: string
  /** 当前是否激活 */
  readonly active: boolean
  /** 取消当前工具会话 */
  cancel(): void
  /**
   * 订阅工具结果变更。
   *
   * @param cb - 结果变更回调
   * @returns 取消订阅的 Disposable
   */
  onResult(cb: (result: T) => void): Disposable
}

// ==================== Camera ====================

/** 相机状态快照 */
export interface CameraState {
  /** 世界坐标位置 */
  position: THREE.Vector3
  /** 观察目标点 */
  target: THREE.Vector3
  /** 相机 up 向量 */
  up: THREE.Vector3
  /** 透视视场角，单位度 */
  fov: number
  /** 正交缩放系数 */
  zoom: number
  /** 相机模式 */
  mode: CameraMode
  /** 相机绕视线方向的旋转角度（弧度），可选 */
  roll?: number
}

/** 相机投影模式 */
export enum CameraMode {
  /** 透视投影 */
  Perspective = 'perspective',
  /** 正交投影 */
  Orthographic = 'orthographic',
}

// ==================== Controls ====================

/** 控制器模式标识，允许字符串扩展以支持自定义控制器 */
export type ControlMode =
  | 'orbit'
  | 'spacemouse'
  | 'first-person'
  | 'legacy-first-person'
  | 'camera-free-look'
  | 'earth'
  | 'map'
  | 'fly'
  | 'none'
  | (string & {})

// ==================== Normalized Input ====================

/** 归一化指针事件 */
export interface NormalizedPointerEvent {
  /** CSS 像素屏幕坐标 */
  screen: THREE.Vector2
  /** active-view / captured-view 兼容层使用的视口矩形，不可用于多视口命中 */
  viewport?: ViewportRect
  /** active-view / captured-view 兼容层使用的 NDC，不可用于多视口命中 */
  ndc: THREE.Vector2
  /** 按键索引，0=左键 1=中键 2=右键 */
  button: number
  /** Shift 是否按下 */
  shiftKey: boolean
  /** Ctrl 是否按下 */
  ctrlKey: boolean
  /** Alt 是否按下 */
  altKey: boolean
  /** 原生 PointerEvent */
  domEvent: PointerEvent
}

/** 归一化滚轮事件 */
export interface NormalizedWheelEvent {
  /** 滚动量，正值表示向前/放大 */
  delta: number
  /** CSS 像素屏幕坐标 */
  screen: THREE.Vector2
  /** active-view / captured-view 兼容层使用的视口矩形，不可用于多视口命中 */
  viewport?: ViewportRect
  /** active-view / captured-view 兼容层使用的 NDC，不可用于多视口命中 */
  ndc: THREE.Vector2
  /** 原生 WheelEvent */
  domEvent: WheelEvent
}

/** 归一化键盘事件 */
export interface NormalizedKeyEvent {
  /** 按键逻辑值（含 layout/输入法） */
  key: string
  /** 按键物理码 */
  code: string
  /** Shift 是否按下 */
  shiftKey: boolean
  /** Ctrl 是否按下 */
  ctrlKey: boolean
  /** Alt 是否按下 */
  altKey: boolean
  /** 原生 KeyboardEvent */
  domEvent: KeyboardEvent
}

// ==================== Input Consumers ====================

/** 输入消费者接口，按优先级依次派发归一化事件 */
export interface InputConsumer {
  /** 消费者名称 */
  readonly name: string
  /** 派发优先级，数值越小越先收到事件 */
  readonly priority: number
  /**
   * 指针按下回调。
   *
   * @param event - 归一化指针事件
   * @returns 返回 true 表示已消费事件，阻止后续派发
   */
  onPointerDown?(event: NormalizedPointerEvent): boolean
  /**
   * 指针移动回调。
   *
   * @param event - 归一化指针事件
   * @returns 返回 true 表示已消费事件
   */
  onPointerMove?(event: NormalizedPointerEvent): boolean
  /**
   * 指针抬起回调。
   *
   * @param event - 归一化指针事件
   * @returns 返回 true 表示已消费事件
   */
  onPointerUp?(event: NormalizedPointerEvent): boolean
  /**
   * 滚轮回调。
   *
   * @param event - 归一化滚轮事件
   * @returns 返回 true 表示已消费事件
   */
  onWheel?(event: NormalizedWheelEvent): boolean
  /**
   * 按键按下回调。
   *
   * @param event - 归一化键盘事件
   * @returns 返回 true 表示已消费事件
   */
  onKeyDown?(event: NormalizedKeyEvent): boolean
  /**
   * 按键抬起回调。
   *
   * @param event - 归一化键盘事件
   * @returns 返回 true 表示已消费事件
   */
  onKeyUp?(event: NormalizedKeyEvent): boolean
  /**
   * 窗口级按键按下回调，不受 focus 限制。
   *
   * @param event - 归一化键盘事件
   * @returns 返回 true 表示已消费事件
   */
  onWinKeyDown?(event: NormalizedKeyEvent): boolean
  /**
   * 窗口级按键抬起回调。
   *
   * @param event - 归一化键盘事件
   * @returns 返回 true 表示已消费事件
   */
  onWinKeyUp?(event: NormalizedKeyEvent): boolean
  /**
   * 双击回调。
   *
   * @param event - 归一化指针事件
   * @returns 返回 true 表示已消费事件
   */
  onDoubleClick?(event: NormalizedPointerEvent): boolean
}

// ==================== Capabilities ====================

/** 可编辑能力：undo/redo 与命令执行 */
export interface Editable {
  /** 撤销最近一次操作 */
  undo(): void
  /** 重做最近一次撤销的操作 */
  redo(): void
  /**
   * 执行命名编辑命令。
   *
   * @param command - 命令名
   * @param params - 命令参数，键值对
   */
  executeCommand(command: string, params?: Record<string, unknown>): void
  /** 是否存在可撤销记录 */
  readonly canUndo: boolean
  /** 是否存在可重做记录 */
  readonly canRedo: boolean
}

/** 可选择能力：查询与同步 entity 选择集 */
export interface Selectable {
  /**
   * 获取当前选中 entity 的 id 列表。
   *
   * @returns 已选中的 entity id 数组
   */
  getSelectedEntityIds(): string[]
  /**
   * 将外部服务的选择集同步到资产内部。
   *
   * @param entityIds - 要同步的 entity id 列表
   */
  syncSelectionFromService(entityIds: string[]): void
}

/** 可配置能力：读取/合并部分配置 */
export interface Configurable<TConfig> {
  /**
   * 读取当前配置。
   *
   * @returns 配置对象的浅拷贝
   */
  getConfig(): TConfig
  /**
   * 合并更新配置。
   *
   * @param config - 配置补丁
   */
  setConfig(config: Partial<TConfig>): void
}

// ==================== Point Cloud Load Types ====================

/** 点云属性集合 */
export interface PointAttributes {
  /** 点属性数组 */
  attributes: PointAttribute[]
}

/** 单个点属性描述 */
export interface PointAttribute {
  /** 属性名 */
  name: string
  /** 底层数据类型（ordinal 对应 PointAttributeTypes 枚举，size 为字节数） */
  type: { ordinal: number; size: number }
  /** 分量数量 */
  numElements: number
  /** 占用字节数 */
  byteSize: number
  /** 属性描述文本 */
  description: string
  /** 当前数据实际取值范围 [min, max] */
  range?: [number, number]
  /** 初始数据取值范围 [min, max] */
  initialRange?: [number, number]
}

/** 点云节点二进制缓冲 */
export interface PointBuffer {
  /** 原始字节缓冲 */
  buffer: ArrayBuffer
  /** 点数量 */
  pointCount: number
  /** 单点跨度，单位字节 */
  stride: number
}

/** 高斯 splat 缓冲，底层为 ArrayBuffer */
export type SplatBuffer = ArrayBuffer

/** 点云加载完成后的聚合结果 */
export interface PointCloudLoadResult {
  /** 元数据 */
  metadata: PointCloudMetadata
  /** 八叉树根节点 */
  root: PointCloudNode
  /** 节点数据按需加载器 */
  nodeLoader: NodeLoader
}

/** 点云元数据 */
export interface PointCloudMetadata {
  /** 资产局部坐标包围盒 */
  boundingBox: THREE.Box3
  /** 点总数 */
  pointCount: number
  /** 属性集合 */
  attributes: PointAttributes
  /** 投影 EPSG 编码或 WKT 字符串 */
  projection?: string
  /** 根节点点间距 */
  spacing?: number
}

/** 点云八叉树节点 */
export interface PointCloudNode {
  /** 节点在父节点 children 中的索引（0~7） */
  readonly index: number
  /** 节点包围盒 */
  readonly boundingBox: THREE.Box3
  /** 节点点数量 */
  readonly pointCount: number
  /** LOD 层级，根为 0 */
  readonly level: number
  /** 子节点数组，长度 8，稀疏节点为 null */
  readonly children: readonly (PointCloudNode | null)[]
  /** 节点数据是否已加载 */
  loaded: boolean
  /** 加载完成后的点缓冲 */
  buffer?: PointBuffer
}

/** 点云节点按需加载器 */
export interface NodeLoader {
  /**
   * 加载指定节点的点数据。
   *
   * @param node - 要加载的八叉树节点
   * @param signal - 取消信号
   * @returns 节点点缓冲
   */
  load(node: PointCloudNode, signal?: AbortSignal): Promise<PointBuffer>
  /** 释放底层资源 */
  dispose(): void
}

// ==================== Gaussian Splat Load Types ====================

/** 高斯 splat 加载完成后的聚合结果 */
export interface GaussianSplatLoadResult {
  /** 元数据 */
  metadata: GaussianSplatMetadata
  /** LOD 根节点 */
  root: SplatNode
  /** 节点数据按需加载器 */
  nodeLoader: SplatNodeLoader
}

/** 高斯 splat 元数据 */
export interface GaussianSplatMetadata {
  /** 资产局部坐标包围盒 */
  boundingBox: THREE.Box3
  /** splat 总数 */
  splatCount: number
}

/** 高斯 splat LOD 节点 */
export interface SplatNode {
  /** 节点在父节点 children 中的索引 */
  readonly index: number
  /** 节点包围盒 */
  readonly boundingBox: THREE.Box3
  /** 节点 splat 数量 */
  readonly splatCount: number
  /** LOD 层级，根为 0 */
  readonly level: number
  /** 子节点数组，稀疏节点为 null */
  readonly children: readonly (SplatNode | null)[]
  /** 节点数据是否已加载 */
  loaded: boolean
}

/** 高斯 splat 节点按需加载器 */
export interface SplatNodeLoader {
  /**
   * 加载指定节点的 splat 数据。
   *
   * @param node - 要加载的 LOD 节点
   * @param signal - 取消信号
   * @returns 节点 splat 缓冲
   */
  load(node: SplatNode, signal?: AbortSignal): Promise<SplatBuffer>
  /** 释放底层资源 */
  dispose(): void
}

// ==================== Annotation ====================

/** 标注数据 */
export interface AnnotationData {
  /** 标注 id，创建时可不传 */
  id?: string
  /** 世界坐标锚点 */
  position: THREE.Vector3
  /** 标题文本 */
  title?: string
  /** 正文内容 */
  content?: string
  /** 是否可见 */
  visible?: boolean
  /** 业务自定义数据（引擎不解析，透传给消费方） */
  data?: Record<string, unknown>
}

/** 标注句柄 */
export interface AnnotationHandle {
  /** 标注 id */
  readonly id: string
  /** 世界坐标锚点 */
  readonly anchor: THREE.Vector3
  /** 是否可见 */
  visible: boolean
}

// ==================== CAD 共享数据类型 ====================

/** CAD 实体类型枚举 */
export type CadEntityType =
  | 'arc'
  | 'spline'
  | 'circle'
  | 'polyline'
  | 'text'
  | 'mText'
  | 'alignDimension'
  | string

/** CAD 线型枚举 */
export type CadLineType = 'solid' | 'dash' | 'dashDot' | 'doubleDash' | string

/** CAD 实体数据（共享层公共结构，与 CAD.IEntity 对齐） */
export interface CadEntityData {
  /** 实体 id */
  id: string
  /** 实体类型 */
  type: CadEntityType
  /** 所属图层 id */
  layerId: string
  /** 颜色取值方式（0=ByLayer、1=ByBlock、2=自定义等，与 CAD 约定对齐） */
  colorMethod: number
  /** 颜色 RGB 整数值 */
  color: number
  /** 线型 */
  lineType: CadLineType
  /** 线宽，单位毫米 */
  lineWidth: number
  /** 实体类型相关的扩展字段 */
  [key: string]: unknown
}

/** CAD 图层数据 */
export interface CadLayerData {
  /** 图层 id */
  id: string
  /** 图层名 */
  name: string
  /** 颜色取值方式 */
  colorMethod: number
  /** 颜色 RGB 整数值 */
  color: number
  /** 线型 */
  lineType: CadLineType
  /** 线宽，单位毫米 */
  lineWidth: number
  /** 是否可见 */
  visible: boolean
  /** 是否锁定 */
  locked: boolean
}

/** CAD 平面元数据 */
export interface CadPlaneData {
  /** 平面原点坐标 */
  origin: { x: number; y: number; z: number }
  /** 平面法线方向 */
  normal: { x: number; y: number; z: number }
  /** 平面 up 方向 */
  up: { x: number; y: number; z: number }
  /** 平面标高 */
  elevation: number
}

/** CAD 序列化数据结构 */
export interface CadMetadata {
  /** 图层列表 */
  layers: CadLayerData[]
  /** 实体列表 */
  entities: CadEntityData[]
  /** 平面元数据 */
  plane: CadPlaneData
}
